'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function RightTriangleCalculator() {
  const t = useTranslations("tools.rightTriangleCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    sideA: '',
    sideB: '',
    sideC: '',
    heightH: '',
    angleAlpha: '',
    angleBeta: '',
    area: '',
    perimeter: '',
    alphaUnit: 'degree',
    betaUnit: 'degree'
  });

  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    secondary: '#7c3aed',
    accent: '#06b6d4',
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(37, 99, 235, 0.2)',
    success: '#10b981',
    error: '#ef4444',
  };

  const toRadians = (angle: number, unit: string): number => {
    if (unit === 'radian') return angle;
    return angle * Math.PI / 180;
  };

  const toDegrees = (radians: number): number => {
    return radians * 180 / Math.PI;
  };

  const solveTriangle = (known: any) => {
    const result: any = { ...known };

    try {
      if (known.a && known.b) {
        result.c = Math.sqrt(known.a * known.a + known.b * known.b);
        result.alpha = Math.atan(known.a / known.b);
        result.beta = Math.atan(known.b / known.a);
        result.h = (known.a * known.b) / result.c;
        result.area = (known.a * known.b) / 2;
        result.perimeter = known.a + known.b + result.c;
      }
      else if (known.a && known.c) {
        if (known.c <= known.a) throw new Error('Invalid triangle');
        result.b = Math.sqrt(known.c * known.c - known.a * known.a);
        result.alpha = Math.asin(known.a / known.c);
        result.beta = Math.acos(known.a / known.c);
        result.h = (known.a * result.b) / known.c;
        result.area = (known.a * result.b) / 2;
        result.perimeter = known.a + result.b + known.c;
      }
      else if (known.b && known.c) {
        if (known.c <= known.b) throw new Error('Invalid triangle');
        result.a = Math.sqrt(known.c * known.c - known.b * known.b);
        result.alpha = Math.acos(known.b / known.c);
        result.beta = Math.asin(known.b / known.c);
        result.h = (result.a * known.b) / known.c;
        result.area = (result.a * known.b) / 2;
        result.perimeter = result.a + known.b + known.c;
      }
      else if (known.a && known.alpha) {
        result.b = known.a / Math.tan(known.alpha);
        result.c = known.a / Math.sin(known.alpha);
        result.beta = Math.PI / 2 - known.alpha;
        result.h = (known.a * result.b) / result.c;
        result.area = (known.a * result.b) / 2;
        result.perimeter = known.a + result.b + result.c;
      }
      else if (known.a && known.beta) {
        result.b = known.a * Math.tan(known.beta);
        result.c = known.a / Math.cos(known.beta);
        result.alpha = Math.PI / 2 - known.beta;
        result.h = (known.a * result.b) / result.c;
        result.area = (known.a * result.b) / 2;
        result.perimeter = known.a + result.b + result.c;
      }
      else if (known.b && known.alpha) {
        result.a = known.b * Math.tan(known.alpha);
        result.c = known.b / Math.cos(known.alpha);
        result.beta = Math.PI / 2 - known.alpha;
        result.h = (result.a * known.b) / result.c;
        result.area = (result.a * known.b) / 2;
        result.perimeter = result.a + known.b + result.c;
      }
      else if (known.b && known.beta) {
        result.a = known.b / Math.tan(known.beta);
        result.c = known.b / Math.sin(known.beta);
        result.alpha = Math.PI / 2 - known.beta;
        result.h = (result.a * known.b) / result.c;
        result.area = (result.a * known.b) / 2;
        result.perimeter = result.a + known.b + result.c;
      }
      else if (known.c && known.alpha) {
        result.a = known.c * Math.sin(known.alpha);
        result.b = known.c * Math.cos(known.alpha);
        result.beta = Math.PI / 2 - known.alpha;
        result.h = (result.a * result.b) / known.c;
        result.area = (result.a * result.b) / 2;
        result.perimeter = result.a + result.b + known.c;
      }
      else if (known.c && known.beta) {
        result.b = known.c * Math.sin(known.beta);
        result.a = known.c * Math.cos(known.beta);
        result.alpha = Math.PI / 2 - known.beta;
        result.h = (result.a * result.b) / known.c;
        result.area = (result.a * result.b) / 2;
        result.perimeter = result.a + result.b + known.c;
      }
      else if (known.area && known.a) {
        result.b = (2 * known.area) / known.a;
        result.c = Math.sqrt(known.a * known.a + result.b * result.b);
        result.alpha = Math.atan(known.a / result.b);
        result.beta = Math.atan(result.b / known.a);
        result.h = (known.a * result.b) / result.c;
        result.perimeter = known.a + result.b + result.c;
      }
      else if (known.area && known.b) {
        result.a = (2 * known.area) / known.b;
        result.c = Math.sqrt(result.a * result.a + known.b * known.b);
        result.alpha = Math.atan(result.a / known.b);
        result.beta = Math.atan(known.b / result.a);
        result.h = (result.a * known.b) / result.c;
        result.perimeter = result.a + known.b + result.c;
      }
      else {
        return null;
      }

      if (isNaN(result.a) || isNaN(result.b) || isNaN(result.c) ||
          result.a <= 0 || result.b <= 0 || result.c <= 0) {
        throw new Error('Invalid triangle');
      }

      return result;
    } catch (e) {
      return null;
    }
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResults(null);

    const values: any = {};
    let count = 0;

    if (inputs.sideA) { values.a = parseFloat(inputs.sideA); count++; }
    if (inputs.sideB) { values.b = parseFloat(inputs.sideB); count++; }
    if (inputs.sideC) { values.c = parseFloat(inputs.sideC); count++; }
    if (inputs.heightH) { values.h = parseFloat(inputs.heightH); count++; }
    if (inputs.area) { values.area = parseFloat(inputs.area); count++; }
    if (inputs.perimeter) { values.perimeter = parseFloat(inputs.perimeter); count++; }

    if (inputs.angleAlpha) {
      values.alpha = toRadians(parseFloat(inputs.angleAlpha), inputs.alphaUnit);
      count++;
    }
    if (inputs.angleBeta) {
      values.beta = toRadians(parseFloat(inputs.angleBeta), inputs.betaUnit);
      count++;
    }

    if (count < 2) {
      setError(t('errorMinValues'));
      return;
    }

    for (let key in values) {
      if (values[key] <= 0 && key !== 'alpha' && key !== 'beta') {
        setError(t('errorPositive'));
        return;
      }
    }

    const result = solveTriangle(values);

    if (!result) {
      setError(t('errorInvalid'));
      return;
    }

    setResults(result);
  };

  const handleClear = () => {
    setInputs({
      sideA: '',
      sideB: '',
      sideC: '',
      heightH: '',
      angleAlpha: '',
      angleBeta: '',
      area: '',
      perimeter: '',
      alphaUnit: 'degree',
      betaUnit: 'degree'
    });
    setResults(null);
    setError('');
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    { marginBottom: '1.5rem' },
    { marginBottom: '2rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const subtitleStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const,
      borderRadius: '1rem 1rem 0 0',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const calculatorContentStyle = getResponsiveStyle(
    { display: 'grid', gap: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const formSectionStyle = getResponsiveStyle(
    {
      background: '#f8fafc',
      borderRadius: '0.75rem',
      border: '2px solid #e2e8f0',
      marginBottom: '1.5rem'
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const formGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      background: 'white',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem' },
    { padding: '0.875rem' },
    { padding: '1rem' }
  );

  const selectStyle = getResponsiveStyle(
    {
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      background: 'white',
      minWidth: '100px'
    },
    { padding: '0.75rem 0.5rem' },
    { padding: '0.875rem 0.5rem' },
    { padding: '1rem 0.5rem' }
  );

  const buttonGroupStyle = {
    display: 'flex',
    gap: '1rem',
    marginTop: '1rem',
    flexDirection: screenSize === 'mobile' ? 'column' as const : 'row' as const
  };

  const calculateButtonStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      minHeight: '44px'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' }
  );

  const clearButtonStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569',
      minHeight: '44px'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' }
  );

  const triangleVisualStyle = getResponsiveStyle(
    {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f8fafc',
      borderRadius: '0.75rem',
      border: '2px solid #e2e8f0'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      gridColumn: screenSize === 'desktop' ? '1 / -1' : 'auto',
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderRadius: '0.75rem',
      border: '2px solid #86efac'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultsGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(4, 1fr)' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.125rem', lineHeight: 1.8 }
  );

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={subtitleStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolHeaderStyle}>
          <h2 style={{ ...h2Style, color: 'white', marginBottom: 0 }}>{t('toolTitle')}</h2>
        </div>

        <div style={calculatorContentStyle}>
          <div>
            <form onSubmit={handleCalculate}>
              <div style={formSectionStyle}>
                <h3 style={{ ...h2Style, fontSize: '1.1rem', marginBottom: '1rem' }}>{t('sidesSection')}</h3>
                <div style={formGridStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <label style={{ fontWeight: 600, color: '#475569', fontSize: '0.95rem' }}>{t('sideA')}</label>
                    <input
                      type="number"
                      step="any"
                      placeholder={t('enterValue')}
                      value={inputs.sideA}
                      onChange={(e) => setInputs({ ...inputs, sideA: e.target.value })}
                      style={inputStyle}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <label style={{ fontWeight: 600, color: '#475569', fontSize: '0.95rem' }}>{t('sideB')}</label>
                    <input
                      type="number"
                      step="any"
                      placeholder={t('enterValue')}
                      value={inputs.sideB}
                      onChange={(e) => setInputs({ ...inputs, sideB: e.target.value })}
                      style={inputStyle}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <label style={{ fontWeight: 600, color: '#475569', fontSize: '0.95rem' }}>{t('hypotenuseC')}</label>
                    <input
                      type="number"
                      step="any"
                      placeholder={t('enterValue')}
                      value={inputs.sideC}
                      onChange={(e) => setInputs({ ...inputs, sideC: e.target.value })}
                      style={inputStyle}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <label style={{ fontWeight: 600, color: '#475569', fontSize: '0.95rem' }}>{t('heightH')}</label>
                    <input
                      type="number"
                      step="any"
                      placeholder={t('enterValue')}
                      value={inputs.heightH}
                      onChange={(e) => setInputs({ ...inputs, heightH: e.target.value })}
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div>

              <div style={formSectionStyle}>
                <h3 style={{ ...h2Style, fontSize: '1.1rem', marginBottom: '1rem' }}>{t('anglesSection')}</h3>
                <div style={formGridStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <label style={{ fontWeight: 600, color: '#475569', fontSize: '0.95rem' }}>{t('angleAlpha')}</label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <input
                        type="number"
                        step="any"
                        placeholder={t('enterValue')}
                        value={inputs.angleAlpha}
                        onChange={(e) => setInputs({ ...inputs, angleAlpha: e.target.value })}
                        style={{ ...inputStyle, flex: 1 }}
                      />
                      <select
                        value={inputs.alphaUnit}
                        onChange={(e) => setInputs({ ...inputs, alphaUnit: e.target.value })}
                        style={selectStyle}
                      >
                        <option value="degree">{t('degree')}</option>
                        <option value="radian">{t('radian')}</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <label style={{ fontWeight: 600, color: '#475569', fontSize: '0.95rem' }}>{t('angleBeta')}</label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <input
                        type="number"
                        step="any"
                        placeholder={t('enterValue')}
                        value={inputs.angleBeta}
                        onChange={(e) => setInputs({ ...inputs, angleBeta: e.target.value })}
                        style={{ ...inputStyle, flex: 1 }}
                      />
                      <select
                        value={inputs.betaUnit}
                        onChange={(e) => setInputs({ ...inputs, betaUnit: e.target.value })}
                        style={selectStyle}
                      >
                        <option value="degree">{t('degree')}</option>
                        <option value="radian">{t('radian')}</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div style={formSectionStyle}>
                <h3 style={{ ...h2Style, fontSize: '1.1rem', marginBottom: '1rem' }}>{t('propertiesSection')}</h3>
                <div style={formGridStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <label style={{ fontWeight: 600, color: '#475569', fontSize: '0.95rem' }}>{t('area')}</label>
                    <input
                      type="number"
                      step="any"
                      placeholder={t('enterValue')}
                      value={inputs.area}
                      onChange={(e) => setInputs({ ...inputs, area: e.target.value })}
                      style={inputStyle}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <label style={{ fontWeight: 600, color: '#475569', fontSize: '0.95rem' }}>{t('perimeter')}</label>
                    <input
                      type="number"
                      step="any"
                      placeholder={t('enterValue')}
                      value={inputs.perimeter}
                      onChange={(e) => setInputs({ ...inputs, perimeter: e.target.value })}
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div>

              <div style={{
                background: '#eff6ff',
                borderLeft: '4px solid #2563eb',
                padding: '1rem',
                borderRadius: '0.5rem',
                fontSize: '0.9rem',
                color: '#1e40af',
                marginBottom: '1rem'
              }}>
                💡 {t('infoNote')}
              </div>

              <div style={buttonGroupStyle}>
                <button type="submit" style={calculateButtonStyle}>{t('calculateButton')}</button>
                <button type="button" onClick={handleClear} style={clearButtonStyle}>{t('clearButton')}</button>
              </div>
            </form>

            {error && (
              <div style={{
                background: '#fef2f2',
                border: '2px solid #fca5a5',
                color: '#991b1b',
                padding: '1rem',
                borderRadius: '0.5rem',
                marginTop: '1rem',
                textAlign: 'center',
                fontWeight: 600
              }}>
                {error}
              </div>
            )}
          </div>

          <div style={triangleVisualStyle}>
            <h3 style={{ marginTop: 0, color: '#1e293b' }}>{t('triangleDiagram')}</h3>
            <div style={{ position: 'relative', width: screenSize === 'mobile' ? '250px' : '300px', height: screenSize === 'mobile' ? '200px' : '250px', margin: '1rem 0' }}>
              <svg viewBox="0 0 300 250" style={{ width: '100%', height: '100%' }}>
                <polygon points="50,200 250,200 250,50" fill="#dbeafe" stroke="#2563eb" strokeWidth="3"/>
                <line x1="250" y1="200" x2="250" y2="50" stroke="#2563eb" strokeWidth="2" strokeDasharray="5,5"/>
                <text x="150" y="220" textAnchor="middle" fill="#1e293b" fontSize="16" fontWeight="600">a</text>
                <text x="270" y="125" textAnchor="start" fill="#1e293b" fontSize="16" fontWeight="600">b</text>
                <text x="130" y="115" textAnchor="middle" fill="#7c3aed" fontSize="16" fontWeight="600">c</text>
                <text x="30" y="210" textAnchor="end" fill="#f59e0b" fontSize="16" fontWeight="600">α</text>
                <text x="250" y="230" textAnchor="middle" fill="#f59e0b" fontSize="16" fontWeight="600">β</text>
                <path d="M 250 190 L 260 190 L 260 200" fill="none" stroke="#1e293b" strokeWidth="2"/>
              </svg>
            </div>
            <p style={{ textAlign: 'center', color: '#64748b', fontSize: '0.9rem', margin: 0 }}>
              {t('rightAngleNote')}
            </p>
          </div>

          {results && (
            <div style={resultsStyle}>
              <h3 style={{ margin: '0 0 1.5rem 0', color: '#166534', fontSize: '1.5rem', textAlign: 'center' }}>
                ✨ {t('resultsTitle')}
              </h3>
              <div style={resultsGridStyle}>
                <div style={{ background: 'white', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #bbf7d0' }}>
                  <span style={{ display: 'block', fontSize: '0.9rem', color: '#166534', fontWeight: 600, marginBottom: '0.5rem' }}>
                    {t('sideA')}
                  </span>
                  <span style={{ display: 'block', fontSize: '1.5rem', color: '#15803d', fontWeight: 700 }}>
                    {results.a.toFixed(4)}
                  </span>
                </div>
                <div style={{ background: 'white', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #bbf7d0' }}>
                  <span style={{ display: 'block', fontSize: '0.9rem', color: '#166534', fontWeight: 600, marginBottom: '0.5rem' }}>
                    {t('sideB')}
                  </span>
                  <span style={{ display: 'block', fontSize: '1.5rem', color: '#15803d', fontWeight: 700 }}>
                    {results.b.toFixed(4)}
                  </span>
                </div>
                <div style={{ background: 'white', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #bbf7d0' }}>
                  <span style={{ display: 'block', fontSize: '0.9rem', color: '#166534', fontWeight: 600, marginBottom: '0.5rem' }}>
                    {t('hypotenuseC')}
                  </span>
                  <span style={{ display: 'block', fontSize: '1.5rem', color: '#15803d', fontWeight: 700 }}>
                    {results.c.toFixed(4)}
                  </span>
                </div>
                <div style={{ background: 'white', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #bbf7d0' }}>
                  <span style={{ display: 'block', fontSize: '0.9rem', color: '#166534', fontWeight: 600, marginBottom: '0.5rem' }}>
                    {t('heightH')}
                  </span>
                  <span style={{ display: 'block', fontSize: '1.5rem', color: '#15803d', fontWeight: 700 }}>
                    {results.h.toFixed(4)}
                  </span>
                </div>
                <div style={{ background: 'white', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #bbf7d0' }}>
                  <span style={{ display: 'block', fontSize: '0.9rem', color: '#166534', fontWeight: 600, marginBottom: '0.5rem' }}>
                    {t('angleAlpha')}
                  </span>
                  <span style={{ display: 'block', fontSize: '1.5rem', color: '#15803d', fontWeight: 700 }}>
                    {toDegrees(results.alpha).toFixed(4)}°
                  </span>
                </div>
                <div style={{ background: 'white', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #bbf7d0' }}>
                  <span style={{ display: 'block', fontSize: '0.9rem', color: '#166534', fontWeight: 600, marginBottom: '0.5rem' }}>
                    {t('angleBeta')}
                  </span>
                  <span style={{ display: 'block', fontSize: '1.5rem', color: '#15803d', fontWeight: 700 }}>
                    {toDegrees(results.beta).toFixed(4)}°
                  </span>
                </div>
                <div style={{ background: 'white', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #bbf7d0' }}>
                  <span style={{ display: 'block', fontSize: '0.9rem', color: '#166534', fontWeight: 600, marginBottom: '0.5rem' }}>
                    {t('area')}
                  </span>
                  <span style={{ display: 'block', fontSize: '1.5rem', color: '#15803d', fontWeight: 700 }}>
                    {results.area.toFixed(4)}
                  </span>
                </div>
                <div style={{ background: 'white', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #bbf7d0' }}>
                  <span style={{ display: 'block', fontSize: '0.9rem', color: '#166534', fontWeight: 600, marginBottom: '0.5rem' }}>
                    {t('perimeter')}
                  </span>
                  <span style={{ display: 'block', fontSize: '1.5rem', color: '#15803d', fontWeight: 700 }}>
                    {results.perimeter.toFixed(4)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={{ ...paragraphStyle, paddingLeft: '1.5rem' }}>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep4')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep5')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <div style={{ ...paragraphStyle, background: '#f0f9ff', padding: '1.5rem', borderRadius: '0.75rem', borderLeft: '4px solid #2563eb' }}>
          <h3 style={{ ...h2Style, fontSize: '1.25rem', marginTop: 0 }}>{t('insightCoreFormulas')}</h3>
          <p style={paragraphStyle}>{t('insightCoreFormulasText')}</p>

          <h3 style={{ ...h2Style, fontSize: '1.25rem' }}>{t('insightAutomation')}</h3>
          <p style={paragraphStyle}>{t('insightAutomationText')}</p>

          <h3 style={{ ...h2Style, fontSize: '1.25rem' }}>{t('insightModernTools')}</h3>
          <p style={paragraphStyle}>{t('insightModernToolsText')}</p>

          <h3 style={{ ...h2Style, fontSize: '1.25rem' }}>{t('insightBestPractices')}</h3>
          <p style={paragraphStyle}>{t('insightBestPracticesText')}</p>

          <h3 style={{ ...h2Style, fontSize: '1.25rem' }}>{t('insightSpecialTriangles')}</h3>
          <p style={paragraphStyle}>{t('insightSpecialTrianglesText')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{ ...h2Style, fontSize: '1.25rem' }}>{t('detailsPythagorean')}</h3>
        <p style={paragraphStyle}>{t('detailsPythagoreanText')}</p>

        <h3 style={{ ...h2Style, fontSize: '1.25rem' }}>{t('detailsTrigonometry')}</h3>
        <p style={paragraphStyle}>{t('detailsTrigonometryText')}</p>

        <h3 style={{ ...h2Style, fontSize: '1.25rem' }}>{t('detailsArea')}</h3>
        <p style={paragraphStyle}>{t('detailsAreaText')}</p>

        <h3 style={{ ...h2Style, fontSize: '1.25rem' }}>{t('detailsApplications')}</h3>
        <ul style={{ ...paragraphStyle, paddingLeft: '1.5rem' }}>
          <li style={{ marginBottom: '0.5rem' }}>{t('detailsApp1')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('detailsApp2')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('detailsApp3')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('detailsApp4')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('detailsApp5')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div>
            <h3 style={{ ...h2Style, fontSize: '1.125rem', color: colors.primary }}>{t('faq1Question')}</h3>
            <p style={paragraphStyle}>{t('faq1Answer')}</p>
          </div>
          <div>
            <h3 style={{ ...h2Style, fontSize: '1.125rem', color: colors.primary }}>{t('faq2Question')}</h3>
            <p style={paragraphStyle}>{t('faq2Answer')}</p>
          </div>
          <div>
            <h3 style={{ ...h2Style, fontSize: '1.125rem', color: colors.primary }}>{t('faq3Question')}</h3>
            <p style={paragraphStyle}>{t('faq3Answer')}</p>
          </div>
          <div>
            <h3 style={{ ...h2Style, fontSize: '1.125rem', color: colors.primary }}>{t('faq4Question')}</h3>
            <p style={paragraphStyle}>{t('faq4Answer')}</p>
          </div>
          <div>
            <h3 style={{ ...h2Style, fontSize: '1.125rem', color: colors.primary }}>{t('faq5Question')}</h3>
            <p style={paragraphStyle}>{t('faq5Answer')}</p>
          </div>
          <div>
            <h3 style={{ ...h2Style, fontSize: '1.125rem', color: colors.primary }}>{t('faq6Question')}</h3>
            <p style={paragraphStyle}>{t('faq6Answer')}</p>
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={{ ...paragraphStyle, paddingLeft: '1.5rem', listStyle: 'none' }}>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://www.graphcalc.com/right-triangle-calculator/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none', fontWeight: 600 }}>
              {t('ref1')}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://www.calculator.net/right-triangle-calculator.html" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none', fontWeight: 600 }}>
              {t('ref2')}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://www.omnicalculator.com/math/right-triangle" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none', fontWeight: 600 }}>
              {t('ref3')}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://www.calculatorsoup.com/calculators/geometry-plane/triangles-right.php" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none', fontWeight: 600 }}>
              {t('ref4')}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://courses.lumenlearning.com/ccbcmd-math-1/chapter/applications-of-right-triangle-trigonometry/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none', fontWeight: 600 }}>
              {t('ref5')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}